import Image from "next/image";
import { ArrowUpRight } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import React from "react";
import hisaniLogoBlack from "../../../../../brand/hisani_logo_black.png";
import { Link } from "../../i18n/navigation";

const productLinks = [
  { key: "stopwatch", href: "/products/stopwatch" },
  { key: "planner", href: "/products/planner" },
  { key: "marketplace", href: "/products/marketplace" },
  { key: "stable", href: "/product/stable" },
] as const;

const exploreLinks = [{ key: "pricing", href: "/pricing" }] as const;

const legalLinks = [
  { key: "terms", href: "/terms" },
  { key: "privacy", href: "/privacy" },
] as const;

export function SiteFooter() {
  const locale = useLocale();
  const t = useTranslations("Common.footer");
  const tCommon = useTranslations("Common");
  const homePath = locale === "ar" ? "/ar" : "/";

  return (
    <footer className="bg-brand text-primary">
      <div className="mx-auto max-w-app px-4 pb-[calc(1.5rem+env(safe-area-inset-bottom,0px))] pt-6 sm:px-6 sm:pb-[calc(2.5rem+env(safe-area-inset-bottom,0px))] sm:pt-10 lg:px-8 lg:pb-[calc(3rem+env(safe-area-inset-bottom,0px))] lg:pt-12">
        <div className="grid grid-cols-2 gap-x-6 gap-y-6 sm:grid-cols-1 sm:gap-8 lg:grid-cols-[1.2fr_0.8fr_0.8fr_1fr] lg:gap-8">
          <div className="col-span-2 sm:col-span-1">
            <a
              href={homePath}
              className="inline-flex min-h-11 items-center gap-3 focus-visible:rounded-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
            >
              <Image
                src={hisaniLogoBlack}
                alt={t("brand")}
                width={409}
                height={200}
                className="h-10 w-auto sm:h-12"
              />
            </a>
            <h2 className="mt-3 max-w-md text-xl font-semibold leading-tight tracking-[-0.035em] sm:mt-5 sm:text-3xl">
              {t("heading")}
            </h2>
            <p className="mt-3 hidden max-w-md text-sm leading-6 text-primary/75 sm:block">
              {t("description")}
            </p>
          </div>

          <nav aria-labelledby="footer-products-heading">
            <h2
              id="footer-products-heading"
              className="font-sans text-xs font-bold uppercase tracking-[0.18em]"
            >
              {t("productsHeading")}
            </h2>
            <ul className="mt-3 space-y-1">
              {productLinks.map(({ key, href }) => (
                <li key={key}>
                  <Link
                    href={href}
                    className="inline-flex min-h-11 items-center text-sm font-medium transition-colors hover:text-primary/65 focus-visible:rounded-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
                  >
                    {t(`products.${key}`)}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <nav aria-labelledby="footer-explore-heading">
            <h2
              id="footer-explore-heading"
              className="font-sans text-xs font-bold uppercase tracking-[0.18em]"
            >
              {t("exploreHeading")}
            </h2>
            <ul className="mt-3 space-y-1">
              {exploreLinks.map(({ key, href }) => (
                <li key={key}>
                  <Link
                    href={href}
                    className="inline-flex min-h-11 items-center text-sm font-medium transition-colors hover:text-primary/65 focus-visible:rounded-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
                  >
                    {t(`explore.${key}`)}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div className="col-span-2 border-t border-primary/25 pt-5 sm:col-span-1 sm:border-t-0 sm:pt-0">
            <h2 className="font-sans text-xs font-bold uppercase tracking-[0.18em]">
              {t("accountHeading")}
            </h2>
            <p className="mt-3 hidden max-w-xs text-sm leading-6 text-primary/75 sm:block">
              {t("accountDescription")}
            </p>
            <Link
              href="/login"
              className="mt-2 inline-flex min-h-11 items-center gap-2 border-b border-primary text-sm font-semibold focus-visible:rounded-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus sm:mt-4"
            >
              {t("accountAction")}
              <ArrowUpRight
                className="size-4 rtl:-scale-x-100"
                aria-hidden="true"
              />
            </Link>
          </div>
        </div>

        <div className="mt-6 flex flex-col gap-3 border-t border-primary/25 pt-4 text-xs text-primary/70 sm:mt-8 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
            <p>
              &copy; {new Date().getFullYear()} {t("brand")}.{" "}
              {t("rightsReserved")}
            </p>
            <nav
              aria-label={tCommon("footerNavigation")}
              className="flex items-center gap-4"
            >
              {legalLinks.map(({ key, href }) => (
                <Link
                  key={key}
                  href={href}
                  className="inline-flex min-h-11 items-center underline decoration-primary/35 underline-offset-4 transition-colors hover:text-primary focus-visible:rounded-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus sm:min-h-0"
                >
                  {tCommon(key)}
                </Link>
              ))}
            </nav>
          </div>
          <p>
            {t.rich("madeIn", {
              sulani: (chunks) => (
                <a
                  href="https://sulanisolutions.com"
                  className="underline decoration-primary/35 underline-offset-4 transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
                >
                  {chunks}
                </a>
              ),
            })}
          </p>
        </div>
      </div>
    </footer>
  );
}
